import React, { useState, useEffect, useRef } from 'react';

import { Box, Flex, useTheme } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import CreateGoalPanel from 'components/MeshList/goals/CreateGoalPanel';
import ObjectiveSidePanel from 'components/ObjectiveSidePanel';
import useForceUpdate from 'components/hooks/useForceUpdate';
import useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges from 'components/hooks/useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { Canvas, Edge, MarkerArrow } from 'reaflow';
import { openCreateOKRPanel, saveNewlyCreatedOkr, useCreateObjectivePanel } from 'reducers/createObjectivePanelSlice';
import { useOKROwnership } from 'reducers/okrsPageSlice';
import { selectTeamPageGlobalFilters } from 'selectors/globalFilters';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { okrsPageSelector } from 'selectors/okrsPage';
import { selectOrgData } from 'selectors/orgData';
import { selectObjectivePanelDetails } from 'selectors/selectObjectivePanelDetails';
import { themeColors } from 'themes/clients/baseTheme';
import { checkUserIsGoalAdmin } from 'utils/helpers/rolesAccess';

import { OpenWith } from 'assets/assetsV1/iconComponents';
import NoDataImg from 'assets/assetsV1/illustrations/noGoals.svg';

import {
  OBJECTIVE_ACCESS_PERMISSIONS,
  OKR_TYPES,
  SEARCH_TAG_TYPES,
  TEAM_DURATIONS_OBJECT,
  TEAM_TASKS_VIEW_TYPES,
} from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { getObjectiveTreeViewData, getOrganizationTags } from 'services/admin';
import { getTextFromTextMacros } from 'services/helpers/post';
import { showErrorSnackbar } from 'services/snackbar';
import { getTeamTasksForListView } from 'services/task';

import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { TextLabel } from 'uiToolkitV1/Typography';

import { getNodesEdgesForTree, toggleNodeGoalsView } from './helpers';
import BaseNode from './nodes/BaseNode';

function truncate(maxLength) {
  return (string) => {
    if (string.length > maxLength) {
      return `${string.substring(0, maxLength)}...`;
    }
    return string;
  };
}

const truncateAfter20Length = truncate(20);

function buildGoal(goal) {
  const goalProgress = goal.getGoalProgress(0);
  return {
    id: goal.id,
    displayName: truncateAfter20Length(getTextFromTextMacros(goal.text, goal.macros)),
    percentage: goalProgress ? `${goalProgress}%` : '0%',
    children: [],
    hasChildren: false,
    type: 'GOAL',
  };
}
const getGoals = (tagType) => (tagId) => (filters) =>
  getTeamTasksForListView({
    tagId,
    tagType,
    limit: 20,
    filters1: filters,
    durationKey: TEAM_DURATIONS_OBJECT.lastMonth,
    offset: 0,
    resourceType: TEAM_TASKS_VIEW_TYPES.goalTree,
  })
    .then((res) => res.map(buildGoal))
    .catch((err) => []);

export const buildFiltersForTeam = (parentTagId) => [
  { type: 'TYPE', operatorType: 'IN', data: ['GOAL'] },
  { type: 'TAGS', operatorType: 'IN', data: [parentTagId] },
];

export const buildFiltersForTag = (parentTagId) => [{ type: 'TYPE', operatorType: 'IN', data: ['GOAL'] }];

function buildObjective(tag) {
  return {
    id: tag.id,
    displayName: tag.displayName || '',
    description: tag.description || '',
    children: [],
    percentage: '',
    hasChildren: true,
  };
}

export function getDisplayNameForTeam(isEmpty) {
  return isEmpty ? 'No objectives present' : 'Objectives';
}

export const getDisplayNameForTags = (tag) => (isEmpty) => tag;

export const OBJECTIVE_GOAL_CREATION_MODAL_CHOICES = ['GOAL', 'OBJECTIVE', 'CANCEL'];

export const buildObjectivesData = (getDisplayName) => (activeTags) => {
  const isEmpty = Array.isArray(activeTags) && activeTags.length === 0;
  const objectives = {
    id: 0,
    displayName: getDisplayName(isEmpty),
    description: 'See Objectives',
    children: activeTags,
    hasChildren: !isEmpty,
  };
  return objectives;
};

// for objectives, in every case teamId will be -1 only
export function getObjectives({ parentTagId = null, teamId = -1 } = {}) {
  return getOrganizationTags({
    teamId,
    tagType: parentTagId ? 'SUB_OBJECTIVE_TAG' : 'GOAL', // we are sending goal but it gets objective
    // tagType: 'SUB_OBJECTIVE_TAG', // need to move this outside somewhere
    orgTagId: parentTagId,
  }).then((allTags) => allTags.filter((tag) => tag.active).map(buildObjective));
}

export const loadChildrenData = (...fns) =>
  // [getObjectives({ parentTagId }), getGoals({ filters })]
  Promise.all(fns.map((fn) => fn())).then(mergeObjectivesAndTags);

export const getGoalsForTeam = (teamId) => getGoals(SEARCH_TAG_TYPES.team)(teamId);
export const getGoalsForTags = (tagId) => getGoals(SEARCH_TAG_TYPES.tag)(tagId);

function mergeObjectivesAndTags(res) {
  const res1 = res[0];
  const res2 = res[1];
  return [...res1, ...res2];
}

/*
  parentTagId: if first node to start from is required, then this is needed
  teamId
*/

const GROUP_BY = {
  uuid: null,
  keyName: 'objective',
  displayName: 'Objective',
  enabled: true,
};

const SPLIT_BY = {
  uuid: null,
  keyName: 'none',
  displayName: 'None',
  enabled: true,
};
export default function TreeView({
  isOpenCreateGoalPanel,
  setIsOpenCreateGoalPanel,
  employee,
  selectedTimeBoundPeriod,
  refreshData,
  refreshFn,
  teamId,
  // for objective panel
  isOpenCreateObjectivePanel,
  setIsOpenCreateObjectivePanel,
  selectedCohort = null,

  setIsGoalPanelOfCreatedObjective,

  setTimeboundDetailsOfCreatedObjective,
}) {
  const location = useLocation();
  const history = useHistory();
  const theme = useTheme();
  const { selectedTimelines } = useSelector(okrsPageSelector);
  const [forceUpdateValue, forceUpdate] = useForceUpdate();
  const [newGoalSubObjective, setNewGoalSubObjective] = useState(undefined);

  const [loading, setLoading] = useState(false);
  const [visibleNodes, setVisibleNodes] = useState([]);
  const [nodesMap, setNodesMap] = useState(null);
  const [visibleEdges, setVisibleEdges] = useState([]);
  const [idTree, setIdTree] = useState(null);
  const [currentTreeIdPath, setCurrentTreeIdPath] = useState(['root']);
  const [currentObjectiveDetails, setCurrentObjectiveDetails] = useState(null);
  const [canvasMaxWidth, setCanvasMaxWidth] = useState(2000);
  const { open, onKeyResultCreation, onObjectiveCreation } = useSelector(selectObjectivePanelDetails);
  const { newOKRDetail } = useCreateObjectivePanel();
  const dispatch = useDispatch();
  const transformWrapperRef = useRef(null);

  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const { objectiveCreationAccess } = useSelector(selectOrgData);

  const showObjectiveOption = () => {
    if (objectiveCreationAccess === OBJECTIVE_ACCESS_PERMISSIONS[0].value) {
      return checkUserIsGoalAdmin();
    }
    if (objectiveCreationAccess === OBJECTIVE_ACCESS_PERMISSIONS[1].value) {
      return checkUserIsGoalAdmin() || loggedInEmployee?.isHRISManager;
    }

    return false;
  };

  /* Access Control code starts here */
  const {
    canCreateObjectiveAtRootLevel,
    canCreateObjectiveUnderKeyResult,
    canCreateObjectiveUnderObjective,
    canCreateKeyResultAtRootLevel,
    canCreateKeyResultUnderKeyResult,
    canCreateKeyResultUnderObjective,
  } = useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges();

  const canCreateObjective =
    canCreateObjectiveAtRootLevel || canCreateObjectiveUnderObjective || canCreateObjectiveUnderKeyResult;

  const canCreateKeyResult =
    canCreateKeyResultAtRootLevel || canCreateKeyResultUnderObjective || canCreateKeyResultUnderKeyResult;

  /* Objective config */

  const enableObjectiveCreation = canCreateObjective;

  const enableKeyResultCreation = canCreateKeyResult;

  useEffect(() => {
    if (newOKRDetail) {
      if (!newOKRDetail?.parentPost) {
        setCurrentTreeIdPath(['root']);
        return;
      }
      let updatedTreeIdPath = [];
      // if parent is not expanded already expand it
      if (!currentTreeIdPath.find((id) => id === newOKRDetail?.parentPost?.uuid)) {
        updatedTreeIdPath = [...currentTreeIdPath, newOKRDetail?.parentPost?.uuid];
        setCurrentTreeIdPath(updatedTreeIdPath);
      } else {
        updatedTreeIdPath = [...currentTreeIdPath];
      }

      if (selectedTimeBoundPeriod) {
        refetchOkrData();
      }

      dispatch(saveNewlyCreatedOkr(null));
    }
  }, [newOKRDetail]);

  const recalculateNodesEdges = ({
    newNodeIdPathArray = currentTreeIdPath,
    newNodesMap = nodesMap,
    newIdTree = idTree,
  }) => {
    if (!newNodesMap || !newIdTree) {
      return;
    }
    const { nodes, edges } = getNodesEdgesForTree({
      nodeIdPathArray: newNodeIdPathArray,
      nodesMap: newNodesMap,
      idTree: newIdTree,
    });
    setVisibleNodes(nodes);
    setVisibleEdges(edges);
    setCurrentTreeIdPath(newNodeIdPathArray);
  };

  const updateNodeGoalsMetaData = ({ nodeIdPathArray }) => {
    const currentNodeId = nodeIdPathArray.slice(-1); // gets last item in array
    if (nodesMap[currentNodeId].data.goalsCount === 0) {
      nodesMap[currentNodeId].data.goalsCount = 1;
    }
    nodesMap[currentNodeId].data.isGoalsExpanded = true;
  };
  const globalFilters = useSelector((state) => selectTeamPageGlobalFilters(state), shallowEqual);
  const ownership = useOKROwnership();

  const {
    data: okrDataTreeView,
    error: okrDataError,
    isFetching: isOkrDataLoading,
    refetch: refetchOkrData,
  } = useQuery([REACT_QUERY_KEYS.okrTreeView, ownership], () =>
    getObjectiveTreeViewData({
      attributeFilter: {
        filterConditions: [
          {
            type: 'NAME',
            operatorType: 'IN',
            data: [employee?.id],
          },
          {
            type: 'TYPE',
            operatorType: 'IN',
            data: ['GOAL'],
          },
        ],
      },

      cohort: selectedCohort,
      groupByKeys: GROUP_BY,
      splitByKeys: SPLIT_BY,
      orderBy: null,
      timeBounds: [selectedTimeBoundPeriod],
      pageType: 'TEAM_HUB',
      enabled: true,
      ownership,
    })
  );

  useEffect(() => {
    if (isOkrDataLoading) {
      setLoading(true);
      return;
    }
    setLoading(false);

    if (okrDataError) {
      console.log('err while processing getObjectiveTreeData', okrDataError);
      dispatch(showErrorSnackbar(okrDataError));
      return;
    }
    if (okrDataTreeView) {
      const { nodesMap, idTree } = okrDataTreeView;
      setNodesMap(nodesMap);
      setIdTree(idTree);
      recalculateNodesEdges({
        newNodeIdPathArray: currentTreeIdPath,
        newNodesMap: nodesMap,
        newIdTree: idTree,
      });
    }
  }, [okrDataTreeView, okrDataError, isOkrDataLoading]);

  useEffect(() => {
    if (!isOkrDataLoading && selectedTimeBoundPeriod) {
      refetchOkrData();
    }
  }, [refreshData, JSON.stringify(globalFilters?.filters), selectedCohort, selectedTimeBoundPeriod, teamId]);

  useEffect(() => {
    if (currentTreeIdPath.length <= 2) {
      // eslint-disable-next-line no-unused-expressions
      transformWrapperRef?.current?.centerView();
    }
  }, [currentTreeIdPath.length, teamId, selectedTimeBoundPeriod]);

  const onGoalCreationClick = ({ objective, nodeIdPathArray }) => {
    dispatch(
      openCreateOKRPanel({
        selectedObjective: {
          uuid: objective.uuid,
          text: objective.title,
          type: objective.type,
          visibilityType: objective.visibilityType,
        },
        selectedTimeBoundPeriod: selectedTimelines?.[0],
        okrPanelType: OKR_TYPES.KEY_RESULT,
        history,
        location,
      })
    );
  };

  // opening OKR creation panel for Objective
  const onObjectiveCreationClick = ({ objective, nodeIdPathArray }) => {
    /* 
      currently backend not sending parentPostId so this won't work commenting it out for now.
      User can realign from inside the creation panel to achieve the same thing.
      const parentPost = {
        uuid: objective?.parentPostId,
        text: objective?.name,
        type: objective?.type,
        visibilityType: objective?.visibilityType
      }; */
    dispatch(
      openCreateOKRPanel({
        selectedObjective: {
          uuid: objective?.uuid,
          text: objective?.title,
          type: objective?.type,
          visibilityType: objective?.visibilityType,
        },
        selectedTimeBoundPeriod: selectedTimelines?.[0],
        okrPanelType: OKR_TYPES.OBJECTIVE,
        onKeyResultCreation: refreshFn,
        onObjectiveCreation: refreshFn,
        history,
        location,
      })
    );
  };

  if (!visibleNodes) {
    return (
      <Box bg="#FFFFFF" pt={2} px={3}>
        <div style={{ paddingBottom: '60px' }}>
          <EmptyScreen
            heading={`You haven't created ${modifyString(
              keyResultsName,
              StringModifier.LOWERCASE_ARTICLE
            )} ${modifyString(keyResultsName, StringModifier.SENTENCE_CASE)} yet`}
            description={`In the beginners mind, there are only possibilities. Create ${modifyString(
              keyResultsName,
              StringModifier.LOWERCASE_ARTICLE
            )} ${modifyString(keyResultsName, StringModifier.SENTENCE_CASE)}, make possibilities real.`}
            illustrationSrc={NoDataImg}
          />
        </div>
      </Box>
    );
  }

  return (
    <>
      <Container>
        <SvgContainer>
          <TransformWrapper
            wheel={{
              disabled: true,
            }}
            doubleClick={{ disabled: true }}
            options={{
              limitToBounds: false,
            }}
            centerOnInit
            ref={transformWrapperRef}
          >
            <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }}>
              <Canvas
                maxHeight={10000}
                nodes={visibleNodes}
                edges={visibleEdges}
                maxWidth={canvasMaxWidth}
                onLayoutChange={(prop) => {
                  if (prop.width > canvasMaxWidth * 0.8) {
                    setCanvasMaxWidth(canvasMaxWidth * 2);
                  }
                }}
                direction="RIGHT"
                node={(nodeProps) => (
                  <BaseNode
                    nodeProps={nodeProps}
                    recalculateNodesEdges={recalculateNodesEdges}
                    isExpanded={currentTreeIdPath.includes(nodeProps.id)}
                    onGoalsExpandToggle={(nodeId) => {
                      // updates node data in place
                      toggleNodeGoalsView({ nodeId, nodesMap });
                      // so these are needed to trigger re-render and for changes to take effect
                      setVisibleNodes((prevVal) => [...prevVal]);
                      setVisibleEdges((prevVal) => [...prevVal]);
                    }}
                    onObjectiveCreationClick={onObjectiveCreationClick}
                    onGoalObjectiveCreationClick={onObjectiveCreationClick}
                    onGoalCreationClick={onGoalCreationClick}
                    forceUpdateValue={forceUpdateValue}
                    enableObjectiveCreation={enableObjectiveCreation}
                    enableKeyResultCreation={enableKeyResultCreation}
                    onDelete={(nodeParentId, nodeId) => {
                      if (!nodeParentId || nodeParentId === 'root') {
                        setCurrentTreeIdPath(['root']);
                        return;
                      }
                      const deletedNodeParent = nodesMap[nodeParentId];
                      const newCurrentTreeIdPath = [];
                      for (const id of currentTreeIdPath) {
                        if (!deletedNodeParent || deletedNodeParent?.id === id) {
                          break;
                        }
                        newCurrentTreeIdPath.push(id);
                      }
                      // more than on child before deletion , show siblings expanded
                      if (deletedNodeParent?.childList?.length > 1) {
                        newCurrentTreeIdPath.push(deletedNodeParent?.id);
                      }

                      setCurrentTreeIdPath(newCurrentTreeIdPath);
                    }}
                    onUpdate={(nodeParentId, nodeId) => {
                      if (!nodeParentId || nodeParentId === 'root') {
                        setCurrentTreeIdPath(['root']);

                        return;
                      }
                      const realignedNodeParent = nodesMap[nodeParentId];
                      const newCurrentTreeIdPath = [];
                      for (const id of currentTreeIdPath) {
                        if (!realignedNodeParent || realignedNodeParent?.id === id) {
                          break;
                        }
                        newCurrentTreeIdPath.push(id);
                      }
                      if (realignedNodeParent?.childList?.length > 1) {
                        newCurrentTreeIdPath.push(realignedNodeParent?.id);
                      }

                      setCurrentTreeIdPath(newCurrentTreeIdPath);
                    }}
                  />
                )}
                style={{ background: '#e5e5e5' }}
                readonly
                arrow={<MarkerArrow style={{ fill: themeColors.surface.g3 }} size={8} />}
                zoomable
                fit
                edge={<Edge style={{ stroke: themeColors.surface.g3, strokeWidth: '2px' }} />}
                // center={false}
              />
            </TransformComponent>
          </TransformWrapper>
        </SvgContainer>
        <InfoOverlayContainer>
          <Flex alignItems="center">
            <OpenWith color={theme.colors.common.black} />
            <TextLabel color={theme.colors.text.g1} marginLeft="8px">
              Click and drag to navigate
            </TextLabel>
          </Flex>
        </InfoOverlayContainer>
      </Container>

      <ObjectiveSidePanel
        open={isOpenCreateObjectivePanel}
        onCancel={() => {
          setTimeboundDetailsOfCreatedObjective(null);
          setIsGoalPanelOfCreatedObjective(false);
          setIsOpenCreateObjectivePanel(false);
        }}
        selectedTimeBoundPeriod={selectedTimelines?.[0] ?? null}
        isAlignmentAtParentLevel
        setIsOpenCreateGoalPanel={(value) => {
          setIsOpenCreateGoalPanel(value);
          setIsGoalPanelOfCreatedObjective(value);
        }}
        setTimeboundDetailsOfCreatedObjective={setTimeboundDetailsOfCreatedObjective}
        source="Team Page"
        objectiveTimeBoundId={currentObjectiveDetails !== null ? currentObjectiveDetails.objectiveTimeBoundId : null}
        uuid={currentObjectiveDetails !== null ? currentObjectiveDetails.id : null}
        addNewObjective={refreshFn}
      />

      <CreateGoalPanel
        open={isOpenCreateGoalPanel}
        onCancel={() => {
          setIsOpenCreateGoalPanel(false);
          setNewGoalSubObjective(undefined);
        }}
        employee={employee}
        afterGoalCreation={forceUpdate}
        subObjective={newGoalSubObjective}
        eventSource="Tree view"
      />
    </>
  );
}

const Container = styled.div`
  position: relative;
  margin-bottom: 1rem;
  margin-top: 24px;
`;

const InfoOverlayContainer = styled(Flex)`
  position: absolute;
  width: 100%;
  top: 32px;
  padding: 0 32px;
  justify-content: space-between;
  align-items: center;
`;

const SvgContainer = styled.div`
  position: relative;
  width: 100%;
  height: 80vh;
  border-radius: 4px;
`;

const StyledCanvas = styled(Canvas)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;
